import { Auth } from 'aws-amplify';
import getEnvVar from '@/utils/getEnvVar';

interface ChatbotRequestOptions {
  method: string;
  body?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isErrorResponse(data: unknown): data is { error: string } {
  return isRecord(data) && 'error' in data && typeof data.error === 'string';
}

async function getCurrentIdToken(): Promise<string> {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
}

function getChatbotApiBaseUrl(): string | undefined {
  return getEnvVar('VITE_CHATBOT_API_URL') ?? getEnvVar('VITE_DATA_API_URL');
}

async function sendChatbotRequest(
  url: string,
  options: ChatbotRequestOptions,
  authToken: string,
  signal?: AbortSignal,
): Promise<Response> {
  return fetch(url, {
    method: options.method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(signal === undefined ? {} : { signal }),
  });
}

async function readChatbotErrorMessage(response: Response): Promise<string> {
  const body: unknown = await response.json().catch(() => ({ error: 'Unknown error' }));

  if (isErrorResponse(body)) {
    return body.error;
  }

  return `Request failed with status ${response.status}`;
}

export type { ChatbotRequestOptions };
export {
  isRecord,
  isErrorResponse,
  sendChatbotRequest,
  readChatbotErrorMessage,
  getCurrentIdToken,
  getChatbotApiBaseUrl,
};
